/* eslint-disable no-nested-ternary */
/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2021 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 *
 * @file SelectComponent.jsx
 * @author Srinivasan Bakthavatchalam
 * @since 1.0.1
 */

import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { injectIntl, intlShape } from 'react-intl';

import FilterActionButton from '../filter/FilterActionButton';
import FilterActionButtonDefault from '../filter/FilterActionButtonDefault';
import StyledCheckBox from '../common/StyledCheckBox';
import usePrevious from '../common/usePrevious';
import {
  BULK_DOWNLOAD_STATUS_COMPLETED,
  BULK_DOWNLOAD_STATUS_ERROR,
  BULK_DOWNLOAD_STATUS_INPROGRESS,
  BULK_DOWNLOAD_STATUS_NOT_STARTED,
  BULK_DOWNLOAD_STATUS_REMOVING,
  DOWNLOAD_STATE_WAIT_TIME,
  DOWNLOAD_STATUS_COMPLETED,
  DOWNLOAD_STATUS_ERROR,
  SELECT_ALL
} from '../../common/constants';
import messages from '../../../translations/defaultMessages';

const styles = (theme) => ({
  selectContainer: {
    display: 'flex',
    margin: '18px 18px 0px 18px',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  selectCheckBox: {
    marginRight: 5
  },
  selectCount: {
    color: theme.color.textPrimary,
    fontFamily: 'TT Commons',
    fontSize: 18,
    lineHeight: '110%'
  },
  selectedDetails: {
    display: 'inline-flex',
    alignItems: 'center'
  },
  removeButton: {
    paddingRight: 5,
    marginRight: 5
  }
});

const SelectComponent = ({
  bulkDownloadStatus,
  bulkDownloadStatusCallback,
  classes,
  currentSelectedResources,
  handleCancel,
  handleDownload,
  handleRemove,
  handleRetry,
  handleRetryCancel,
  hasDownloadedResource,
  intl,
  progressCount,
  selectAll,
  selectedResources,
  updateSelectedResource
}) => {
  const prevBulkDownloadStatus = usePrevious(bulkDownloadStatus);
  const [selected, setSelected] = useState(false);
  const [timeUp, setTimeUp] = useState(true);
  const handleClick = () => {
    if (selectedResources.length > 0 || selected) {
      updateSelectedResource([], true);
      setSelected(false);
    } else {
      updateSelectedResource(SELECT_ALL, true);
      setSelected(true);
    }
  };
  const totalCount = currentSelectedResources.length;
  const currenResourceFailed = currentSelectedResources.filter(resource => resource.status === DOWNLOAD_STATUS_ERROR);
  const { formatMessage } = intl;

  useEffect(() => {
    if (bulkDownloadStatus === BULK_DOWNLOAD_STATUS_COMPLETED
      && (prevBulkDownloadStatus === BULK_DOWNLOAD_STATUS_INPROGRESS
        || prevBulkDownloadStatus === BULK_DOWNLOAD_STATUS_ERROR)) {
      setTimeUp(false);
      setSelected(false);
      setTimeout(() => {
        setTimeUp(true);
      }, DOWNLOAD_STATE_WAIT_TIME);
    }

    if (bulkDownloadStatus === BULK_DOWNLOAD_STATUS_COMPLETED
      && prevBulkDownloadStatus === BULK_DOWNLOAD_STATUS_REMOVING) {
      setSelected(false);
    }
    if (prevBulkDownloadStatus !== bulkDownloadStatus) {
      bulkDownloadStatusCallback(bulkDownloadStatus);
    }
  }, [bulkDownloadStatus]);
  const handleDownloadStart = () => {
    handleDownload();
  };
  const updateTextAndButtonName = () => {
    let buttonName = formatMessage(messages.cancel);
    let progressText = `${progressCount} ${formatMessage(messages.of)} ${totalCount} ${formatMessage(messages.downloading)}`;

    if (bulkDownloadStatus === BULK_DOWNLOAD_STATUS_ERROR) {
      buttonName = formatMessage(messages.retry);
      progressText = `${currenResourceFailed.length} ${formatMessage(messages.downloadFaild)}`;
    } else if (bulkDownloadStatus === BULK_DOWNLOAD_STATUS_REMOVING) {
      progressText = `${formatMessage(messages.removing)} ${progressCount} ${formatMessage(messages.of)} ${totalCount}...`;
    }

    return ({ buttonName, progressText });
  };
  const handleCancelOrRetryClick = () => {
    setTimeUp(true);

    if (bulkDownloadStatus === BULK_DOWNLOAD_STATUS_ERROR) {
      handleRetry();
    } else {
      handleCancel();
    }
  };
  const isFirstSelectedResourceDownloaded = (selectedResources.length === 0 || (selectedResources.length > 0
    && selectedResources[0].status === DOWNLOAD_STATUS_COMPLETED));
  const isFirstSelectedResourceNotDownloadable = (selectedResources.length === 0 || (selectedResources.length > 0
    && selectedResources[0].status !== DOWNLOAD_STATUS_COMPLETED));
  const hasFailedResource = currenResourceFailed.length > 0;
  const resultMessage = bulkDownloadStatus === BULK_DOWNLOAD_STATUS_COMPLETED && hasFailedResource
    ? formatMessage(messages.downloadError) : formatMessage(messages.downloadSuccess);

  return (
    <div className={classes.selectContainer}>
      {bulkDownloadStatus === BULK_DOWNLOAD_STATUS_COMPLETED
        || bulkDownloadStatus === BULK_DOWNLOAD_STATUS_NOT_STARTED ?
        timeUp ?
          <Fragment>
            <div className={classes.selectedDetails}>
              <StyledCheckBox
                checked={selected}
                className={classes.selectCheckBox}
                indeterminate={selectedResources.length > 0 && !selectAll}
                onClick={handleClick}
              />
              <span className={classes.selectCount}>{selectedResources.length} {formatMessage(messages.selected)}</span>
            </div>
            <div>
              {hasDownloadedResource &&
              <FilterActionButtonDefault
                className={classes.removeButton}
                color="default"
                disabled={isFirstSelectedResourceNotDownloadable}
                onClick={handleRemove}
                size="large"
                variant="outlined"
              >
                {formatMessage(messages.remove)}
              </FilterActionButtonDefault>
            }
              <FilterActionButton
                color="primary"
                disabled={isFirstSelectedResourceDownloaded}
                onClick={handleDownloadStart}
                size="large"
                variant="outlined"
              >
                {formatMessage(messages.download)}
              </FilterActionButton>
            </div>
          </Fragment> :
          <span className={classes.selectCount}> {resultMessage} </span>
        :
          <Fragment>
            <span className={classes.selectCount}>
              {updateTextAndButtonName().progressText}
            </span>
            <div>
              {bulkDownloadStatus === BULK_DOWNLOAD_STATUS_ERROR &&
              <FilterActionButtonDefault
                className={classes.removeButton}
                color="default"
                onClick={handleRetryCancel}
                size="large"
                variant="outlined"
              >
                {formatMessage(messages.cancel)}
              </FilterActionButtonDefault>
              }
              <FilterActionButton
                color="primary"
                onClick={handleCancelOrRetryClick}
                size="large"
                variant="outlined"
              >
                {updateTextAndButtonName().buttonName}
              </FilterActionButton>
            </div>
          </Fragment>}
    </div>);
};

SelectComponent.propTypes = {
  bulkDownloadStatus: PropTypes.string.isRequired,
  bulkDownloadStatusCallback: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
  currentSelectedResources: PropTypes.array,
  handleCancel: PropTypes.func.isRequired,
  handleDownload: PropTypes.func.isRequired,
  handleRemove: PropTypes.func.isRequired,
  handleRetry: PropTypes.func.isRequired,
  handleRetryCancel: PropTypes.func.isRequired,
  hasDownloadedResource: PropTypes.bool.isRequired,
  intl: intlShape,
  progressCount: PropTypes.number,
  selectAll: PropTypes.bool.isRequired,
  selectedResources: PropTypes.array.isRequired,
  updateSelectedResource: PropTypes.func.isRequired
};
SelectComponent.defaultProps = {
  currentSelectedResources: [],
  intl: {},
  progressCount: 0
};

export default withStyles(styles)(injectIntl(SelectComponent));
